<!-- Admin / bare mode: standard .form-control, no floating label.
     Meant to sit inside <app-form-row>, which supplies the static label. -->
<ng-container *ngIf="control && bare">
  <!-- Numeric variant. `type` is STATIC here on purpose: Angular matches
       directive selectors against the template, and `input[type=number]` can
       never match a bound `[type]`, so NumberValueAccessor, MinValidator and
       MaxValidator all fail to attach on the generic input below. That is why
       every migrated numeric field held a string instead of a number and why
       `min`/`max` produced no validation at all (#11331). `[min]`/`[max]` are
       property bindings, not `[attr.]`, so the validators see them. -->
  <input
    *ngIf="!multiline && isNumeric"
    #bareInput
    type="number"
    [id]="inputId"
    [min]="min"
    [max]="max"
    [attr.inputmode]="inputMode"
    [attr.data-testid]="testId"
    [class.form-control]="!plain"
    [ngClass]="cssClass"
    [class.is-invalid]="showError"
    [autocomplete]="autocomplete"
    [formControl]="control"
    (keyup)="onKeyup($event)"
    (keydown)="keydownEvent.emit($event)"
    (change)="onChange($event)"
    (input)="onInput($event)"
    (focus)="focusEvent.emit($event)"
    (focusout)="onFocusout($event)"
    (blur)="onBlur($event)"
    [placeholder]="placeholder"
    [attr.disabled]="isDisabled ? true : null"
    [readonly]="readonly"
  />
  <input
    *ngIf="!multiline && !isNumeric"
    #bareInput
    [id]="inputId"
    [type]="inputType"
    [attr.inputmode]="inputMode"
    [attr.min]="min"
    [attr.max]="max"
    [attr.data-testid]="testId"
    [class.form-control]="!plain"
    [ngClass]="cssClass"
    [class.is-invalid]="showError"
    [autocomplete]="autocomplete"
    [maxLength]="maxLength"
    [formControl]="control"
    (keyup)="onKeyup($event)"
    (keydown)="keydownEvent.emit($event)"
    (change)="onChange($event)"
    (input)="onInput($event)"
    (focus)="focusEvent.emit($event)"
    (focusout)="onFocusout($event)"
    (blur)="onBlur($event)"
    [trim]="trim"
    [placeholder]="placeholder"
    [attr.disabled]="isDisabled ? true : null"
    [readonly]="readonly"
  />
  <!-- Bare multiline: same fitMultilineTextarea as floating (no CDK). -->
  <textarea
    *ngIf="multiline"
    #bareTextarea
    [id]="inputId"
    [attr.data-testid]="testId"
    [attr.inputmode]="inputMode"
    [attr.autocorrect]="autocorrect"
    [attr.spellcheck]="spellcheck"
    [class.form-control]="!plain"
    [ngClass]="cssClass"
    [class.is-invalid]="showError"
    [autocomplete]="autocomplete"
    [maxLength]="maxLength"
    [rows]="minRows"
    [formControl]="control"
    (keyup)="onKeyup($event)"
    (keydown)="keydownEvent.emit($event)"
    (change)="onChange($event)"
    (input)="onMultilineInput($event)"
    (focus)="focusEvent.emit($event)"
    (focusout)="onFocusout($event)"
    (blur)="onBlur($event)"
    [trim]="trim"
    [placeholder]="placeholder"
    [attr.disabled]="isDisabled ? true : null"
    [readonly]="readonly"
  ></textarea>
  <div
    class="invalid-feedback d-block"
    [attr.data-testid]="errorTestId"
    *ngIf="showError"
  >
    <span *ngIf="errorText?.trim()">{{ errorText }}</span>
    <span *ngIf="!errorText?.trim()">{{
      errorMessageKey | translate: errorMessageParams
    }}</span>
  </div>
  <app-explanation *ngIf="helpText" [text]="helpText"></app-explanation>
</ng-container>

<!-- Standalone bare mode: no FormControl — parent drives [value] and reads
     events (mirrors the checkbox/toggle standalone convention). -->
<input
  *ngIf="!control && bare && !multiline"
  #bareInput
  [id]="inputId"
  [type]="inputType"
  [attr.inputmode]="inputMode"
  [attr.min]="min"
  [attr.max]="max"
  [attr.data-testid]="testId"
  [class.form-control]="!plain"
  [ngClass]="cssClass"
  [class.is-invalid]="isInvalid"
  [autocomplete]="autocomplete"
  [maxLength]="maxLength"
  [value]="value ?? ''"
  (keyup)="onKeyup($event)"
  (keydown)="keydownEvent.emit($event)"
  (change)="onChange($event)"
  (input)="onInput($event)"
  (focus)="focusEvent.emit($event)"
  (focusout)="onFocusout($event)"
  (blur)="onBlur($event)"
  [placeholder]="placeholder"
  [attr.disabled]="isDisabled ? true : null"
  [readonly]="readonly"
/>

<!-- Floating-label mode (default; customer UI) -->
<div
  class="input-group mb-4"
  [class.is-multiline]="multiline"
  *ngIf="control && !bare"
>
  <!-- Multiline: same label, same populated/focus float as the input below,
       so the label shrinks exactly like the single-line fields. -->
  <!-- Floating note: no cdkTextareaAutosize — its ngDoCheck freezes #cart.
       Grow via fitMultilineTextarea (same as bare). -->
  <textarea
    *ngIf="multiline"
    #floatingTextarea
    [id]="inputId"
    class="form-control"
    [ngClass]="{
      'is-invalid': isInvalid,
      populated: hasValue,
    }"
    [attr.data-testid]="testId"
    [attr.inputmode]="inputMode"
    [attr.autocorrect]="autocorrect"
    [attr.spellcheck]="spellcheck"
    [autocomplete]="autocomplete"
    [maxLength]="maxLength"
    [rows]="minRows"
    [formControl]="control"
    (keyup)="onKeyup($event)"
    (change)="onChange($event)"
    (input)="onMultilineInput($event)"
    (focus)="focusEvent.emit($event)"
    (focusout)="onFocusout($event)"
    (blur)="onBlur($event)"
    [placeholder]="placeholder"
    [attr.disabled]="isDisabled ? true : null"
  ></textarea>
  <input
    *ngIf="!multiline"
    [id]="inputId"
    [type]="inputType"
    class="form-control"
    [ngClass]="{
      'is-invalid': isInvalid,
      populated: hasValue,
    }"
    [autocomplete]="autocomplete"
    [maxLength]="maxLength"
    [formControl]="control"
    (keyup)="onKeyup($event)"
    (change)="onChange($event)"
    (focusout)="onFocusout($event)"
    (blur)="onBlur($event)"
    [trim]="trim"
    [placeholder]="placeholder"
    [attr.disabled]="isDisabled ? true : null"
  />
  <label [for]="inputId" class="placeholder font-size-primary">
    {{ label }}
    <ng-container *ngIf="required">*</ng-container>
    <ng-container *ngIf="!required && optionalTag">{{
      'public.global.optional' | translate
    }}</ng-container>
  </label>
  <div
    class="invalid-feedback font-size-secondary"
    [attr.data-testid]="errorTestId"
    *ngIf="isInvalid && errorText?.trim()"
  >
    <span>{{ errorText }}</span>
  </div>
</div>
